import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { SheetFooter } from '@/components/ui/sheet'
import { Trash2 } from 'lucide-react'
import { AGENT_URL } from '@/config/env'
import { ApiError, mutateJsonWithAuth } from '@/lib/api'
import { isAllowedIntakeUrl } from '@/features/connections/CredentialIntakeSheet'
import { updateTool } from '@/features/tools/api/api'
import type { CalcomAppTool } from '@/features/tools/types'
import { orgToolsQueryKey } from '@/hooks/useToolsAdmin'

type Intake = { intake_url: string; contract: string }
type KeyState = { credential_version: string | null }

/* oxlint-disable react/iframe-missing-sandbox -- The validated cross-origin Mono form requires scripts and session-origin semantics. */
export function CalcomToolForm({
  tool: initial,
  onClose,
  onDelete,
}: {
  tool: CalcomAppTool
  onClose: () => void
  onDelete?: () => void
}) {
  const { getToken } = useAuth()
  const cache = useQueryClient()
  const tool = initial
  const [name, setName] = useState(initial.name)
  const [eventUrl, setEventUrl] = useState(initial.settings.event_url)
  const [angelica, setAngelica] = useState(initial.angelica_enabled)
  const [keyState, setKeyState] = useState<
    'loading' | 'unknown' | 'present' | 'absent'
  >('loading')
  const hasKey = keyState === 'present'
  const keyRevision = useRef(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [intake, setIntake] = useState<Intake | null>(null)
  const [receipt, setReceipt] = useState<string | null>(null)
  const iframe = useRef<HTMLIFrameElement>(null)
  const completing = useRef(false)
  const target = useRef(`calcom-intake-${crypto.randomUUID()}`)

  const post = useCallback(
    async <T,>(path: string, body: unknown = {}): Promise<T> => {
      const token = await getToken()
      if (!token) throw new Error('Please sign in again.')
      return mutateJsonWithAuth<T>({
        token,
        url: `${AGENT_URL}${path}`,
        method: 'POST',
        body,
        errorMessage: 'Unable to save this scheduling tool.',
      })
    },
    [getToken]
  )

  useEffect(() => {
    if (!initial) return
    let active = true
    const revision = keyRevision.current
    void post<KeyState>(`/tools/${initial.id}/calcom/status`)
      .then((state) => {
        if (active && revision === keyRevision.current)
          setKeyState(state.credential_version ? 'present' : 'absent')
        return undefined
      })
      .catch(() => {
        if (active && revision === keyRevision.current) {
          setKeyState('unknown')
          setError(
            'Unable to check the saved key. Reopen this tool to try again.'
          )
        }
      })
    return () => {
      active = false
    }
  }, [initial, post])

  useEffect(() => {
    if (!intake || !iframe.current) return
    const form = document.createElement('form')
    const input = document.createElement('input')
    form.method = 'POST'
    form.action = intake.intake_url
    form.target = target.current
    form.hidden = true
    input.name = 'contract'
    input.type = 'hidden'
    input.value = intake.contract
    form.append(input)
    document.body.append(form)
    try {
      form.submit()
    } finally {
      form.remove()
    }
  }, [intake])

  const complete = useCallback(
    async (value: string) => {
      if (!tool || completing.current) return
      completing.current = true
      setBusy(true)
      setError(null)
      setReceipt(value)
      try {
        const result = await post<KeyState>(
          `/tools/${tool.id}/calcom/complete`,
          {
            receipt: value,
          }
        )
        if (!result.credential_version)
          throw new Error('The key could not be attached.')
        keyRevision.current += 1
        setKeyState('present')
        setIntake(null)
        setReceipt(null)
      } catch (cause) {
        if (
          cause instanceof ApiError &&
          cause.status >= 400 &&
          cause.status < 500
        ) {
          setReceipt(null)
          setIntake(null)
        }
        setError(
          cause instanceof Error
            ? cause.message
            : 'Unable to finish saving the key.'
        )
      } finally {
        completing.current = false
        setBusy(false)
      }
    },
    [tool, post]
  )

  useEffect(() => {
    if (!intake) return
    const origin = new URL(intake.intake_url).origin
    const listener = (event: MessageEvent<unknown>) => {
      if (
        event.source !== iframe.current?.contentWindow ||
        event.origin !== origin
      )
        return
      const data = event.data
      if (
        !data ||
        typeof data !== 'object' ||
        !('type' in data) ||
        data.type !== 'careforce:tool-key:saved' ||
        !('receipt' in data) ||
        typeof data.receipt !== 'string' ||
        data.receipt.length > 8192
      )
        return
      void complete(data.receipt)
    }
    window.addEventListener('message', listener)
    return () => window.removeEventListener('message', listener)
  }, [intake, complete])

  async function launch() {
    setBusy(true)
    setError(null)
    setReceipt(null)
    setIntake(null)
    try {
      const link = await post<Intake>(`/tools/${tool.id}/calcom/intake`)
      const url = new URL(link.intake_url)
      if (
        !isAllowedIntakeUrl(url) ||
        url.pathname !== '/tools/intake' ||
        url.search ||
        url.hash
      )
        throw new Error('Secure key entry is unavailable.')
      setIntake(link)
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Secure key entry is unavailable.'
      )
    } finally {
      setBusy(false)
    }
  }

  async function save() {
    if (!tool || !hasKey) return
    setBusy(true)
    setError(null)
    try {
      const token = await getToken()
      if (!token) throw new Error('Please sign in again.')
      await updateTool(token, tool.id, {
        name,
        settings: { version: 1, event_url: eventUrl },
        angelica_enabled: angelica,
      })
      await cache.invalidateQueries({ queryKey: orgToolsQueryKey })
      onClose()
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Unable to save this tool.'
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="flex-1 space-y-5 overflow-y-auto px-6 py-4">
        <div className="space-y-2">
          <Label>Features</Label>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            <li>Check available times</li>
            <li>Book an appointment after confirmation</li>
            <li>Check the status of a booking made with this tool</li>
          </ul>
        </div>
        <div className="space-y-2">
          <Label htmlFor="calcom-name">Name</Label>
          <Input
            id="calcom-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={busy}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="calcom-event">Cal.com event URL</Label>
          <Input
            id="calcom-event"
            placeholder="https://cal.com/your-name/intro"
            value={eventUrl}
            onChange={(event) => setEventUrl(event.target.value)}
            disabled={busy}
          />
        </div>
        <div className="rounded-lg border border-neutral-ui-border p-4">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm">
              {keyState === 'present'
                ? 'API key saved'
                : keyState === 'absent'
                  ? 'API key required'
                  : keyState === 'loading'
                    ? 'Checking API key…'
                    : 'Couldn’t check key status'}
            </span>
            <Button
              type="button"
              variant="outline"
              disabled={busy || !name.trim() || !eventUrl.trim()}
              onClick={() => void launch()}>
              {hasKey
                ? 'Replace key'
                : keyState === 'absent'
                  ? 'Add API key'
                  : 'Enter API key'}
            </Button>
          </div>
          {intake && (
            <iframe
              ref={iframe}
              name={target.current}
              title="Secure Cal.com API key"
              sandbox="allow-forms allow-same-origin allow-scripts"
              referrerPolicy="no-referrer"
              className="mt-3 h-64 w-full border-0"
            />
          )}
          {receipt && error && (
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => void complete(receipt)}>
              Finish saving key
            </Button>
          )}
          {intake && (
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setIntake(null)
                setReceipt(null)
                setError(null)
              }}>
              Cancel key entry
            </Button>
          )}
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="calcom-angelica">Enabled for Angelica</Label>
          <Switch
            id="calcom-angelica"
            checked={angelica}
            onCheckedChange={setAngelica}
            disabled={busy}
          />
        </div>
        {busy && (
          <p role="status" className="text-sm">
            Saving…
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
      <SheetFooter className="shrink-0 border-t border-border bg-neutral-ui-bg">
        <div className="flex items-center justify-between gap-2">
          {onDelete ? (
            <Button variant="destructive" onClick={onDelete} disabled={busy}>
              <Trash2 className="size-4" /> Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={() => void save()}
              disabled={
                busy ||
                !hasKey ||
                Boolean(intake) ||
                !name.trim() ||
                !eventUrl.trim()
              }>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </SheetFooter>
    </>
  )
}
